import { Observable, skip, takeUntil } from 'rxjs';

import { skipWhile } from 'rxjs/operators';

import { AbstractControl } from '@angular/forms';
import { cloneDeep, isEqual } from 'lodash-es';

export class UtilityHelper {
	static trackByFunction(index, item) {
		return item.id;
	}

	static deepCopy(item: any) {
		if (item) {
			return cloneDeep(item);
		}

		return null;
	}

	static deepCopySimple(item: any) {
		if (item) {
			return JSON.parse(JSON.stringify(item));
		}

		return null;
	}

	static isEqual(item1, item2) {
		return isEqual(item1, item2);
	}

	static isEqualSimple(item1, item2) {
		return JSON.stringify(item1) === JSON.stringify(item2);
	}

	static isIos() {
		if (window.navigator.userAgent.match(/ipad|iphone|ipod/i)) {
			const iOS = {};
			return true;
		}
		return false;
	}

	static isObjectEmpty(object) {
		return Object.keys(object).length === 0;
	}

	static isObjectUndefined(object) {
		return typeof object === 'undefined';
	}

	static isDateObject(date) {
		let isDateObject = false;
		if (Object.prototype.toString.call(date) === '[object Date]') {
			isDateObject = true; // it is a date
			if (isNaN(date)) {
				// d.getTime() or d.valueOf() will also work
				isDateObject = false; // date object is not valid
			} else {
				isDateObject = true; // date object is valid
			}
		} else {
			isDateObject = false; // not a date object
		}

		return isDateObject;
	}

	/**
	 * Checking value type equals to Number
	 */
	static isNumber(value: any): boolean {
		return !isNaN(this.toInteger(value));
	}

	static isGuid(val: string): boolean {
		var regExPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
		return regExPattern.test(val) === true;
	}

	/**
	 * Covert value to number
	 */
	static toInteger(value: any): number {
		return parseInt(`${value}`, 10);
	}

	static getPrecision(value: number) {
		// Returns number of decimal places
		if (!isFinite(value)) return 0;
		var e = 1,
			p = 0;
		while (Math.round(value * e) / e !== value) {
			e *= 10;
			p++;
		}
		return p;
	}

	static capitalizeFirstLetter(word: string) {
		return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
	}

	static sortArrayByDate(array: any[], propertyName: string, ascending: boolean = true) {
		array = this.deepCopy(array);
		if (ascending) {
			array.sort(function (a, b) {
				var key1 = new Date(a[propertyName]);
				var key2 = new Date(b[propertyName]);

				if (key1 < key2) {
					return -1;
				} else if (key1 == key2) {
					return 0;
				} else {
					return 1;
				}
			});
		} else {
			array.sort(function (a, b) {
				var key1 = new Date(b[propertyName]);
				var key2 = new Date(a[propertyName]);

				if (key1 < key2) {
					return -1;
				} else if (key1 == key2) {
					return 0;
				} else {
					return 1;
				}
			});
		}
		return array;
	}

	static stringIsNullOrWhitespace(s: string | null | undefined): boolean {
		return !s || !s.trim();
	}

	static newGuid(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			var r = (Math.random() * 16) | 0,
				v = c == 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	/**
	 * Creates a new array of items sorted by most to least similar to the queryText, using "fuzzy" matching logic.
	 * Additionally, if an item is missing any word from the queryText (as an exact substring), that item is filtered
	 * out.
	 * If the queryText is nullish or whitespace the original order of items is preserved.
	 *
	 * ## Example
	 * ```
	 * Given a queryText of:
	 *   "red cat 4"
	 *
	 * And items mapped to:
	 *   item1 => "red cat"     //   Missing "4"; exclude item1.
	 *   item2 => "red cat 4"   //✅ Matches exactly; 1st match.
	 *   item3 => "4"           //   Missing "red" and "cat"; exclude item3.
	 *   item4 => "red 42 cat"  //✅ All words match but not in order. 2nd match.
	 *                          //   Note that "4" matches "42" as a substring.
	 *
	 * The result array will be two items:
	 *   [0] = item2;
	 *   [1] = item4;
	 *
	 * ```
	 *
	 * @template T - The type of the items in the array.
	 * @param {T[]} items - An array of items to be filtered and sorted.
	 * @param {(item: T) => string} itemText - Maps an item to a string for similarity comparison.
	 * @param {string | null | undefined} queryText - The text to compare to each item for similarity.
	 * @returns {T[]} - Returns a new array of filtered and sorted items.
	 */
	static filterSortByTextSimilarity<T>(items: T[], itemText: (item: T) => string, queryText: string | null | undefined): T[] {
		if (UtilityHelper.stringIsNullOrWhitespace(queryText)) return [...items];

		const queryTokens = queryText!.split(/\s+/);

		const scored = items
			.map((item) => {
				const sourceTokens = itemText(item).split(/\s+/);
				return {
					item: item,
					score: UtilityHelper.calculateTextSimilarity(sourceTokens, queryTokens),
				};
			})
			.filter((x) => x.score !== -Infinity);

		scored.sort((a, b) => b.score - a.score);

		return scored.map((x) => x.item);
	}

	static generateUuid() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0,
				v = c == 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	static emptyGuid() {
		return '00000000-0000-0000-0000-000000000000';
	}

	static sleep(milliseconds: number): Promise<any> {
		return new Promise((resolve) => setTimeout(resolve, milliseconds));
	}

	private static calculateTextSimilarity(sourceTokens: string[], queryTokens: string[]): number {
		if (sourceTokens.length === queryTokens.length && sourceTokens.every((val, index) => val === queryTokens[index])) return Infinity;

		const someQueryTokenIsNotASubstringOfAnySourceToken = queryTokens.some((searchToken) => !sourceTokens.some((actualToken) => actualToken.includes(searchToken)));
		if (someQueryTokenIsNotASubstringOfAnySourceToken) return -Infinity;

		const nGramSizes = [2, 3];
		return nGramSizes.reduce((score, nGramSize) => score + UtilityHelper.calculateNGramSimilarity(sourceTokens, queryTokens, nGramSize), 0);
	}

	private static calculateNGramSimilarity(tokens1: string[], tokens2: string[], nGramSize: number): number {
		const grams1 = UtilityHelper.getNGrams(tokens1, nGramSize);
		const grams2 = UtilityHelper.getNGrams(tokens2, nGramSize);

		const intersection = grams1.filter((value) => grams2.includes(value));
		const union = [...new Set([...grams1, ...grams2])];

		const jaccardIndex = intersection.length / union.length;

		const unionStringLength = union.reduce((acc, cur) => acc + cur.length, 0);

		return jaccardIndex * unionStringLength;
	}

	private static getNGrams(words: string[], n: number): string[] {
		if (n === 1) return [...words];

		const nGrams = [];
		for (let i = 0; i <= words.length - n; i++) {
			nGrams.push(words.slice(i, i + n).join(' '));
		}
		return nGrams;
	}

	static insertStyleTagDom(idSelector: string, style: string) {
		let existingStyleElement: HTMLElement;
		existingStyleElement = document.getElementById(idSelector);

		if (existingStyleElement) {
			existingStyleElement.remove();
		}

		let styleElement = document.createElement('style');
		styleElement.id = idSelector;
		styleElement.appendChild(document.createTextNode(style));
		document.head.appendChild(styleElement);
	}

	static sanitizeString(input: string): string {
		return input.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
	}

	static refreshApp() {
		window.location.href = '/';
	}

	static reloadPage() {
		window.location.reload();
	}

	static openInNewTab(url: string) {
		window.open(url, '_blank');
	}

	static openInNewWindow(url: string, width: number = 1280, height: number = 800) {
		window.open(url, '_blank', `width=${width}  height=${height} resizable scrollbars menubar=yes`);
	}

	static removeWhitespace(input: string): string {
		return input.replace(/[\n\r\t]/g, '');
	}

	static stripQueryParamsFromUrl(url) {
		return url.split('?')[0];
	}

	static removePortFromUrl(url) {
		const urlObject = new URL(url);
		urlObject.port = '';
		return urlObject.href;
	}

	/**
	 * Waits until an object property exists with timeout
	 * @param obj Object to check
	 * @param propertyPath Dot notation path to property (e.g. 'chart.ref')
	 * @param maxAttempts Maximum number of attempts
	 * @param interval Milliseconds between attempts
	 * @returns Promise that resolves when property exists or rejects on timeout
	 */
	static async waitForProperty(obj: any, propertyPath: string, maxAttempts: number = 10, interval: number = 50): Promise<any> {
		return new Promise((resolve, reject) => {
			let attempts = 0;

			const checkProperty = () => {
				attempts++;

				// Split path into parts and check each level
				const parts = propertyPath.split('.');
				let current = obj;

				for (const part of parts) {
					if (current && part in current) {
						current = current[part];
					} else {
						// Property doesn't exist yet
						if (attempts >= maxAttempts) {
							reject(new Error(`Timeout waiting for ${propertyPath}`));
							return;
						}

						// Try again after interval
						setTimeout(checkProperty, interval);
						return;
					}
				}

				// Property exists
				resolve(current);
			};

			checkProperty();
		});
	}
}

export function takeUntilAfter<T>(items$: Observable<T>, predicate: (value: T) => boolean) {
	return (source: Observable<any>) =>
		source.pipe(
			takeUntil(
				items$.pipe(
					skipWhile((x) => !predicate(x)),
					skip(1)
				)
			)
		);
}

export function setEnabled(control: AbstractControl, enabled: boolean, opts?: { onlySelf?: boolean; emitEvent?: boolean }) {
	if (control.enabled !== enabled) enabled ? control.enable(opts) : control.disable(opts);
}
